import { useMutation, useQuery } from '@tanstack/react-query';
import { AxiosInstance } from 'axios';
import { useAxiosClientsContextProvider } from 'shared/axios/axios-clients-provider';

import Role from 'features/role/domain/models/role';

const roleBaseUrl = '/api/v1/Role';

enum RoleQueryPaths {
  CreateRole = 'NewRole',
  GetRoles = 'GetRoles',
  ReadRolesForTemporaryAccess = 'ReadRolesForTemporaryAccess',
  UpdateRole = 'UpdateRole',
  DeleteRole = 'DeleteRole',
}

const createRole = async (axiosInstance: AxiosInstance, role: Role): Promise<Role> => {
  const response = await axiosInstance.post(`${roleBaseUrl}/${RoleQueryPaths.CreateRole}`, role);
  return response.data;
};

const getRoles = async (axiosInstance: AxiosInstance): Promise<Role[]> => {
  const response = await axiosInstance.get(`${roleBaseUrl}/${RoleQueryPaths.GetRoles}`);
  return response.data;
};

const updateRole = async (axiosInstance: AxiosInstance, params: { originalIdpId: string; role: Role }): Promise<Role> => {
  const response = await axiosInstance.put(`${roleBaseUrl}/${RoleQueryPaths.UpdateRole}`, {
    ...params.role,
    originalIdpId: params.originalIdpId,
  });
  return response.data;
};

const deleteRole = async (axiosInstance: AxiosInstance, idpId: string): Promise<void> => {
  await axiosInstance.delete(`${roleBaseUrl}/${RoleQueryPaths.DeleteRole}/${idpId}`);
};

export const useCreateRole = () => {
  const { authenticatedInstance } = useAxiosClientsContextProvider();
  return useMutation({
    mutationKey: [RoleQueryPaths.CreateRole],
    mutationFn: (role: Role) => createRole(authenticatedInstance, role),
  });
};

export const useGetRoles = () => {
  const { authenticatedInstance } = useAxiosClientsContextProvider();
  return useQuery({
    queryKey: [RoleQueryPaths.GetRoles],
    queryFn: () => getRoles(authenticatedInstance),
  });
};

export const useUpdateRole = () => {
  const { authenticatedInstance } = useAxiosClientsContextProvider();
  return useMutation({
    mutationKey: [RoleQueryPaths.UpdateRole],
    mutationFn: (params: { originalIdpId: string; role: Role }) => updateRole(authenticatedInstance, params),
  });
};

export const useDeleteRole = () => {
  const { authenticatedInstance } = useAxiosClientsContextProvider();
  return useMutation({
    mutationKey: [RoleQueryPaths.DeleteRole],
    mutationFn: (idpId: string) => deleteRole(authenticatedInstance, idpId),
  });
};
